import React, { useState, useMemo, useEffect } from "react";
import { Card, CardBody, Row, Spinner } from "reactstrap";
// import Loader from "Components/Common/Loader";
import TableContainer from "Components/Common/TableContainer";
import { getStatusBadge } from "utils/getStatusBatch";
import { formatCurrency } from "utils/formatCurrency";
import Icon from "Components/Icons/Icon";
import { BillingInvoice } from "types/biling.api";

interface BillingHistoryProps {
  billingDetails: BillingInvoice[];
  isLoadingData: boolean;
  currentPage: number;
  nextPage?: string | null;
  handleDownload: (invoiceUrl: string) => void;
  downLoadUrls: string[];
  handlePageChange: (page: number) => void;
}

const BillingHistory = ({
  billingDetails,
  isLoadingData = false,
  currentPage = 1,
  nextPage,
  handleDownload,
  downLoadUrls,
  handlePageChange,
}: BillingHistoryProps) => {
  const [billingData, setBillingData] = useState<BillingInvoice[] | []>([]);

  useEffect(() => {
    setBillingData(() => billingDetails);
  }, [billingDetails]);

  const columns = useMemo(
    () => [
      {
        header: "INVOICE ID",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "DATE",
        accessorKey: "issued_at",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cell: any) => {
          const timestamp = cell.getValue();
          const date = new Date(timestamp);
          return (
            <span className="fw-normal text-black2D fs-14 font-primary">
              {date.toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </span>
          );
        },
      },
      {
        header: "STATUS",
        accessorKey: "paid_at",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const paidAt = cell.getValue();
          const status = paidAt ? "Paid" : "Pending";
          return getStatusBadge(status);
        },
      },
      {
        header: "AMOUNT",
        accessorKey: "total_amount",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const amount = cell.getValue();
          const value = amount?.gross || 0;
          return (
            <span className="fw-normal text-black2D fs-14 font-primary">
              {formatCurrency(value, amount?.currency)}
            </span>
          );
        },
      },
      {
        header: "ACTION",
        enableSorting: false,
        cell: (cell: any) => {
          const invoiceUrl = cell.row.original.invoice_url;
          return (
            <button
              className="btn btn-link p-0"
              onClick={() => handleDownload(invoiceUrl)}
              disabled={downLoadUrls.includes(invoiceUrl)}
            >
              {downLoadUrls.includes(invoiceUrl) ? (
                <Spinner size="sm" color="primary" />
              ) : (
                <Icon name="Download" className="btn-press-effect" />
              )}
            </button>
          );
        },
      },
    ],
    [downLoadUrls, handleDownload],
  );

  document.title = "Billing History | EaziQuote";

  return (
    <React.Fragment>
      <Card id="billingList" className="br-10 bg-white">
        <CardBody className="pt-0">
          <div>
            <TableContainer
              columns={columns}
              data={billingData}
              isGlobalFilter={true}
              customPageSize={billingData.length}
              divClass="table-responsive table-card mb-2 bg-white"
              tableClass="align-middle table-nowrap table-borderless category-table"
              thClass="text-grey89 fw-medium fs-14 font-primary bg-greyF5 category-table"
              theadClass="table-light"
              currentPage={currentPage}
              leftContent={
                <p className="fw-medium text-black2D fs-16 font-primary mt-3">
                  Billing History
                </p>
              }
              hideSearch={true}
              // isInitialLoading={isLoadingData}
              showPagination={false}
            />

            {billingDetails && billingDetails.length > 0 && (
              <Row className="align-items-center mt-2 g-3 text-center text-sm-start d-flex justify-content-end">
                <div className="col-sm-auto">
                  <ul className="pagination pagination-separated pagination-md justify-content-center justify-content-sm-start mb-0">
                    {/* Previous */}
                    <li
                      className={
                        currentPage === 1 ? "page-item disabled" : "page-item"
                      }
                    >
                      <button
                        className="page-link"
                        onClick={(e) => {
                          e.preventDefault();
                          (currentPage || 1) > 1 &&
                            handlePageChange?.((currentPage || 1) - 1);
                        }}
                      >
                        Previous
                      </button>
                    </li>

                    {/* Dynamic Pages */}
                    <li className={`page-item active`}>
                      <button className="page-link">{currentPage}</button>
                    </li>

                    <li
                      className={!nextPage ? "page-item disabled" : "page-item"}
                    >
                      <button
                        className="page-link"
                        onClick={(e) => {
                          e.preventDefault();
                          nextPage &&
                            handlePageChange?.((currentPage || 1) + 1);
                        }}
                      >
                        Next
                      </button>
                    </li>
                  </ul>
                </div>
              </Row>
            )}
          </div>
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

export default BillingHistory;
